import { format } from "date-fns";
import {
  extractLogoColors,
  getDefaultColorPalette,
  lightenColor,
  darkenColor,
} from "./colorExtraction";

// Helper function to draw rounded rectangles
const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

// Helper function to generate sophisticated school color palette with modern design principles
const generateSchoolColorPalette = async (school, logoImage) => {
  // Use our new color extraction utility
  try {
    if (school?.picture?.path) {
      const extractedColors = await extractLogoColors(school.picture.path);
      console.log("Extracted colors from logo:", extractedColors);

      // Convert our color format to the expected format
      return {
        // Primary brand colors
        primary: `rgb(${extractedColors.primary[0]}, ${extractedColors.primary[1]}, ${extractedColors.primary[2]})`,
        secondary: `rgb(${extractedColors.secondary[0]}, ${extractedColors.secondary[1]}, ${extractedColors.secondary[2]})`,
        accent: `rgb(${extractedColors.accent[0]}, ${extractedColors.accent[1]}, ${extractedColors.accent[2]})`,
        neutral: `rgb(${extractedColors.darkGray[0]}, ${extractedColors.darkGray[1]}, ${extractedColors.darkGray[2]})`,

        // Light variations (for backgrounds and subtle elements)
        primaryLight: `rgb(${extractedColors.primaryLight[0]}, ${extractedColors.primaryLight[1]}, ${extractedColors.primaryLight[2]})`,
        secondaryLight: `rgb(${extractedColors.secondaryLight[0]}, ${extractedColors.secondaryLight[1]}, ${extractedColors.secondaryLight[2]})`,
        accentLight: `rgb(${extractedColors.primaryLight[0]}, ${extractedColors.primaryLight[1]}, ${extractedColors.primaryLight[2]})`,
        neutralLight: `rgb(${extractedColors.lightGray[0]}, ${extractedColors.lightGray[1]}, ${extractedColors.lightGray[2]})`,

        // Dark variations (for text and emphasis)
        primaryDark: `rgb(${extractedColors.primaryDark[0]}, ${extractedColors.primaryDark[1]}, ${extractedColors.primaryDark[2]})`,
        secondaryDark: `rgb(${extractedColors.primaryDark[0]}, ${extractedColors.primaryDark[1]}, ${extractedColors.primaryDark[2]})`,
        accentDark: `rgb(${extractedColors.primaryDark[0]}, ${extractedColors.primaryDark[1]}, ${extractedColors.primaryDark[2]})`,
        neutralDark: `rgb(${extractedColors.text[0]}, ${extractedColors.text[1]}, ${extractedColors.text[2]})`,

        // Ultra-light variations (for backgrounds)
        primaryUltraLight: `rgb(${extractedColors.lightGray[0]}, ${extractedColors.lightGray[1]}, ${extractedColors.lightGray[2]})`,
        secondaryUltraLight: `rgb(${extractedColors.lightGray[0]}, ${extractedColors.lightGray[1]}, ${extractedColors.lightGray[2]})`,
        neutralUltraLight: `rgb(${extractedColors.lightGray[0]}, ${extractedColors.lightGray[1]}, ${extractedColors.lightGray[2]})`,

        // High contrast variations (for accessibility)
        primaryHighContrast: `rgb(${extractedColors.primary[0]}, ${extractedColors.primary[1]}, ${extractedColors.primary[2]})`,
        secondaryHighContrast: `rgb(${extractedColors.secondary[0]}, ${extractedColors.secondary[1]}, ${extractedColors.secondary[2]})`,
        neutralHighContrast: `rgb(${extractedColors.text[0]}, ${extractedColors.text[1]}, ${extractedColors.text[2]})`,
      };
    }
  } catch (error) {
    console.error("Error extracting colors:", error);
  }

  // Fallback to modern default palette
  return {
    // Primary brand colors
    primary: "#2563eb",
    secondary: "#1e40af",
    accent: "#f59e0b",
    neutral: "#64748b",

    // Light variations (for backgrounds and subtle elements)
    primaryLight: "#60a5fa",
    secondaryLight: "#3b82f6",
    accentLight: "#fbbf24",
    neutralLight: "#94a3b8",

    // Dark variations (for text and emphasis)
    primaryDark: "#1e3a8a",
    secondaryDark: "#1e40af",
    accentDark: "#d97706",
    neutralDark: "#1f2937",

    // Ultra-light variations (for backgrounds)
    primaryUltraLight: "#dbeafe",
    secondaryUltraLight: "#dbeafe",
    neutralUltraLight: "#f8fafc",

    // High contrast variations (for accessibility)
    primaryHighContrast: "#1e3a8a",
    secondaryHighContrast: "#1e40af",
    neutralHighContrast: "#1f2937",
  };
};

// Helper function to draw default logo
const drawDefaultLogo = (ctx, x, y, size) => {
  ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${size / 3}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.fillText("📚", x + size / 2, y + size / 2 - 5);
};

// Helper function to draw information fields with modern design
const drawInfoField = (ctx, label, value, x, y, maxWidth, bold = false, colorPalette) => {
  // Label with modern typography
  ctx.fillStyle = colorPalette.neutralDark;
  ctx.font = '600 10px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.textAlign = "left";
  ctx.fillText(label, x, y);

  // Modern underline with gradient
  const underlineGradient = ctx.createLinearGradient(x, y + 18, x + maxWidth, y + 18);
  underlineGradient.addColorStop(0, colorPalette.neutralUltraLight);
  underlineGradient.addColorStop(0.5, colorPalette.accentLight);
  underlineGradient.addColorStop(1, colorPalette.neutralUltraLight);

  ctx.strokeStyle = underlineGradient;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x, y + 18);
  ctx.lineTo(x + maxWidth, y + 18);
  ctx.stroke();

  // Value with enhanced typography
  ctx.fillStyle = colorPalette.primaryDark;
  ctx.font = `${bold ? "700 " : "500 "}12px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif`;

  // Truncate text if too long with modern ellipsis
  let displayValue = value;
  const maxChars = Math.floor(maxWidth / 6.5);
  if (displayValue.length > maxChars) {
    displayValue = displayValue.substring(0, maxChars - 3) + "…";
  }

  ctx.fillText(displayValue, x, y + 24);
};

// Helper function to format address
const formatAddress = (address) => {
  if (!address) return "School Address";

  const parts = [address.address_line, address.city, address.state].filter(
    (part) => part && part.trim(),
  );

  return parts.join(", ") || "School Address";
};

// Helper function to load an image with a timeout, used for logos/photos/signatures
const loadImageWithTimeout = (src, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    const timeoutId = setTimeout(() => {
      reject(new Error("Image load timeout"));
    }, timeout);

    img.onload = () => {
      clearTimeout(timeoutId);
      resolve(img);
    };

    img.onerror = () => {
      clearTimeout(timeoutId);
      reject(new Error("Image load failed"));
    };

    img.src = src;
  });
};

/**
 * Shared front/back CR80 ID card renderer used by both generateStudentIDCard and
 * generateStaffIDCard. The two card types are drawing-operation-identical — they
 * differ only in which text/labels/fields get plugged in — so this is the single
 * place the actual canvas layout lives. Config shape:
 *
 * {
 *   subject: { photoPath, fullName, gender },
 *   school,
 *   qrCodeDataUrl,
 *   cardTypeLabel: "STUDENT ID CARD" | "STAFF ID CARD",
 *   identityVerticalLabel: "STUDENT IDENTITY CARD" | "STAFF IDENTITY CARD",
 *   infoFields: [{ label, value }, { label, value }, { label, value }], // exactly 3, below NAME
 *   instructions: string[],       // back-card bullet lines (blank strings = spacer)
 *   instructionsTitle: string,    // e.g. "IMPORTANT INSTRUCTIONS" | "TERMS & CONDITIONS"
 *   instructionLineHeight, instructionStartOffset, qrSectionOffset, // back-card layout
 *     previously diverged slightly here (student: +30/16/35, staff: +25/15/30) —
 *     preserved via config rather than silently unified, since unifying would
 *     visibly shift existing printed-card output. qrSectionOffset is a fixed
 *     offset from the instructions block start, independent of how many
 *     instruction lines there are (does NOT scale with instructions.length).
 *   emergencyLabel: string,       // e.g. "EMERGENCY" | "EMERGENCY / SCHOOL CONTACT"
 *   qrCaption: string,            // e.g. "QR CODE" | "STAFF QR CODE"
 *   qrSubtitle: string,           // e.g. "Scan for attendance" | "Scan for Clock In / Out"
 *   signatureLabel: string,       // e.g. "Principal Signature" | "Authorised Signature"
 * }
 */
const drawIDCard = (config) => {
  const {
    subject,
    school,
    qrCodeDataUrl,
    cardTypeLabel,
    identityVerticalLabel,
    infoFields,
    instructions,
    instructionsTitle,
    instructionLineHeight,
    instructionStartOffset,
    qrSectionOffset,
    qrCaption,
    qrSubtitle,
    signatureLabel,
  } = config;

  if (!qrCodeDataUrl || !school || !subject) return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    // Create canvas - landscape orientation to fit both front and back
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Set high resolution for sharp images and text
    const pixelRatio = 2; // Fixed ratio for consistency
    const displayWidth = 1200;
    const displayHeight = 680;

    // Set actual canvas size (high resolution)
    canvas.width = displayWidth * pixelRatio;
    canvas.height = displayHeight * pixelRatio;

    // Scale canvas back to display size
    canvas.style.width = displayWidth + "px";
    canvas.style.height = displayHeight + "px";

    // Scale the drawing context to match device pixel ratio
    ctx.scale(pixelRatio, pixelRatio);

    // Enable high-quality rendering
    ctx.textBaseline = "top";
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Background - clean white
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, displayWidth, displayHeight);

    // Card dimensions
    const cardWidth = 540; // CR80 standard ratio
    const cardHeight = 340;
    const cardSpacing = 60;
    const cornerRadius = 16;

    // Position cards side by side
    const frontCardX = 30;
    const backCardX = frontCardX + cardWidth + cardSpacing;
    const cardY = (displayHeight - cardHeight) / 2;

    // Load QR code image
    const qrImage = new Image();
    qrImage.crossOrigin = "anonymous";
    qrImage.onload = async () => {
      try {
        // Load school logo
        let schoolLogoImage = null;
        if (school?.picture?.path) {
          try {
            schoolLogoImage = await loadImageWithTimeout(school.picture.path, 5000);
          } catch (error) {
            console.log("School logo failed to load:", error);
            schoolLogoImage = null;
          }
        }

        // Load subject photo
        let subjectPhotoImage = null;
        if (subject.photoPath) {
          try {
            subjectPhotoImage = await loadImageWithTimeout(subject.photoPath, 5000);
          } catch (error) {
            console.log("Subject photo failed to load:", error);
            subjectPhotoImage = null;
          }
        }

        // Generate color palette from school logo
        const colorPalette = await generateSchoolColorPalette(school, schoolLogoImage);
        console.log("Generated color palette:", colorPalette);

        // Draw cards with loaded logo and subject photo
        drawFrontCard(schoolLogoImage, subjectPhotoImage, colorPalette);
        drawBackCard(colorPalette);

        function drawFrontCard(logoImage, photoImage, palette) {
          const cardX = frontCardX;

          // Card shadow (multiple layers for depth)
          for (let i = 0; i < 3; i++) {
            ctx.fillStyle = `rgba(0, 0, 0, ${0.03 * (3 - i)})`;
            drawRoundedRect(
              ctx,
              cardX + i * 2,
              cardY + i * 2 + 4,
              cardWidth,
              cardHeight,
              cornerRadius,
            );
            ctx.fill();
          }

          // Main card background
          ctx.fillStyle = "#ffffff";
          drawRoundedRect(ctx, cardX, cardY, cardWidth, cardHeight, cornerRadius);
          ctx.fill();

          // Header section with solid color (drawn first to avoid border overlap)
          const headerHeight = 80;
          ctx.fillStyle = palette.primary;

          // Draw header with rounded top corners only
          ctx.beginPath();
          ctx.moveTo(cardX + cornerRadius, cardY);
          ctx.lineTo(cardX + cardWidth - cornerRadius, cardY);
          ctx.quadraticCurveTo(cardX + cardWidth, cardY, cardX + cardWidth, cardY + cornerRadius);
          ctx.lineTo(cardX + cardWidth, cardY + headerHeight);
          ctx.lineTo(cardX, cardY + headerHeight);
          ctx.lineTo(cardX, cardY + cornerRadius);
          ctx.quadraticCurveTo(cardX, cardY, cardX + cornerRadius, cardY);
          ctx.fill();

          // Create gradient border for modern look
          const borderGradient = ctx.createLinearGradient(
            cardX,
            cardY,
            cardX + cardWidth,
            cardY + cardHeight,
          );
          borderGradient.addColorStop(0, palette.primary);
          borderGradient.addColorStop(0.3, palette.accent);
          borderGradient.addColorStop(0.7, palette.secondary);
          borderGradient.addColorStop(1, palette.primaryDark);

          ctx.strokeStyle = borderGradient;
          ctx.lineWidth = 3; // Slightly thicker for modern look
          ctx.lineCap = "round";
          ctx.lineJoin = "round";

          // Draw border with subtle inset for depth
          drawRoundedRect(
            ctx,
            cardX + 2,
            cardY + 2,
            cardWidth - 4,
            cardHeight - 4,
            cornerRadius - 2,
          );
          ctx.stroke();

          // Add subtle inner border for refinement
          ctx.strokeStyle = palette.neutralUltraLight;
          ctx.lineWidth = 1;
          drawRoundedRect(
            ctx,
            cardX + 4,
            cardY + 4,
            cardWidth - 8,
            cardHeight - 8,
            cornerRadius - 3,
          );
          ctx.stroke();

          ctx.restore(); // Restore context state

          // School logo in header
          const logoSize = 60;
          const logoX = cardX + 20;
          const logoY = cardY + 15;

          // Always draw the default logo first, then try to load the school logo
          drawDefaultLogo(ctx, logoX, logoY, logoSize);

          // Try to load school logo if available
          if (logoImage) {
            // Create circular clipping path for logo with high quality
            ctx.save();
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            ctx.beginPath();
            ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
            ctx.restore();

            // Logo border
            ctx.strokeStyle = "#ffffff";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
            ctx.stroke();
          }

          // School name in header with enhanced typography
          ctx.fillStyle = "#ffffff";
          ctx.font = '700 18px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "left";
          const schoolName = school?.school_name || "SCHOOL NAME";
          ctx.fillText(schoolName.toUpperCase(), logoX + logoSize + 15, cardY + 20);

          // Card type text with modern styling
          ctx.font = '500 14px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
          ctx.fillText(cardTypeLabel, logoX + logoSize + 15, cardY + 40);

          // School year with modern styling
          const currentYear = new Date().getFullYear();
          ctx.font = '500 11px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.fillStyle = palette.accentLight;
          ctx.fillText(`${currentYear}/${currentYear + 1}`, logoX + logoSize + 15, cardY + 58);

          // Identity text vertically on left side - centered in white region with modern styling
          ctx.save();
          // Calculate the white region (card height minus header height)
          const whiteRegionHeight = cardHeight - headerHeight;
          const whiteRegionCenterY = cardY + headerHeight + whiteRegionHeight / 2;
          ctx.translate(cardX + 15, whiteRegionCenterY);
          ctx.rotate(-Math.PI / 2);

          // Add subtle shadow for depth
          ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;

          ctx.fillStyle = palette.primary;
          ctx.font = '700 15px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "center";
          ctx.fillText(identityVerticalLabel, 0, 0);

          // Reset shadow
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;

          ctx.restore();

          // Main content area
          const contentY = cardY + headerHeight + 20;

          // Subject photo with modern styling (bigger rectangular)
          const photoWidth = 120;
          const photoHeight = 140;
          const photoX = cardX + 50;
          const photoY = contentY + 5;

          // Photo frame with modern design
          ctx.fillStyle = palette.neutralUltraLight;
          drawRoundedRect(ctx, photoX - 3, photoY - 3, photoWidth + 6, photoHeight + 6, 12);
          ctx.fill();

          ctx.strokeStyle = palette.neutralLight;
          ctx.lineWidth = 1;
          drawRoundedRect(ctx, photoX - 3, photoY - 3, photoWidth + 6, photoHeight + 6, 12);
          ctx.stroke();

          // Draw subject photo or placeholder
          if (photoImage) {
            // Draw actual photo with rounded rectangular clipping
            ctx.save();
            drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, 8);
            ctx.clip();

            // Draw the photo with proper scaling and cropping
            const aspectRatio = photoImage.width / photoImage.height;
            let drawWidth = photoWidth;
            let drawHeight = photoHeight;
            let drawX = photoX;
            let drawY = photoY;

            if (aspectRatio > photoWidth / photoHeight) {
              // Image is wider than photo area - fit height
              drawHeight = photoHeight;
              drawWidth = photoHeight * aspectRatio;
              drawX = photoX - (drawWidth - photoWidth) / 2;
            } else {
              // Image is taller than photo area - fit width
              drawWidth = photoWidth;
              drawHeight = photoWidth / aspectRatio;
              drawY = photoY - (drawHeight - photoHeight) / 2;
            }

            ctx.drawImage(photoImage, drawX, drawY, drawWidth, drawHeight);
            ctx.restore();

            // Photo border
            ctx.strokeStyle = palette.neutralLight;
            ctx.lineWidth = 2;
            drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, 8);
            ctx.stroke();
          } else {
            // Photo placeholder with modern styling
            ctx.fillStyle = palette.neutralUltraLight;
            drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, 8);
            ctx.fill();

            // Photo icon with modern styling
            ctx.fillStyle = palette.neutral;
            ctx.font = "bold 40px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.fillText("👤", photoX + photoWidth / 2, photoY + photoHeight / 2 - 15);
          }

          // Information fields with modern design
          const infoX = photoX + photoWidth + 25;
          const infoStartY = contentY + 15;

          drawInfoField(
            ctx,
            "NAME",
            subject.fullName.toUpperCase(),
            infoX,
            infoStartY,
            250,
            true,
            palette,
          );

          infoFields.forEach((field, index) => {
            drawInfoField(
              ctx,
              field.label,
              field.value,
              infoX,
              infoStartY + 50 + index * 50,
              250,
              false,
              palette,
            );
          });

          // Emergency contact at bottom with modern styling
          const emergencyY = cardY + cardHeight - 65;
          ctx.fillStyle = palette.accentDark;
          ctx.font = '700 11px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "left";
          ctx.fillText(config.emergencyLabel, photoX, emergencyY);

          ctx.fillStyle = palette.primaryDark;
          ctx.font = '500 11px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          const emergencyPhone = school?.phone_number || "08100000088";
          ctx.fillText(emergencyPhone, photoX, emergencyY + 16);

          // School address at bottom with refined styling
          const address = formatAddress(school?.address?.main);
          ctx.fillStyle = palette.neutral;
          ctx.font = '500 10px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "center";
          ctx.fillText(address, cardX + cardWidth / 2, cardY + cardHeight - 15);
        }

        function drawBackCard(palette) {
          const cardX = backCardX;

          // Card shadow
          for (let i = 0; i < 3; i++) {
            ctx.fillStyle = `rgba(0, 0, 0, ${0.03 * (3 - i)})`;
            drawRoundedRect(
              ctx,
              cardX + i * 2,
              cardY + i * 2 + 4,
              cardWidth,
              cardHeight,
              cornerRadius,
            );
            ctx.fill();
          }

          // Main card background
          ctx.fillStyle = "#ffffff";
          drawRoundedRect(ctx, cardX, cardY, cardWidth, cardHeight, cornerRadius);
          ctx.fill();

          // Header section
          const headerHeight = 40;
          const headerGradient = ctx.createLinearGradient(
            cardX,
            cardY,
            cardX + cardWidth,
            cardY + headerHeight,
          );
          headerGradient.addColorStop(0, palette.primary);
          headerGradient.addColorStop(1, palette.secondary);
          ctx.fillStyle = headerGradient;

          // Draw header with rounded top corners only
          ctx.beginPath();
          ctx.moveTo(cardX + cornerRadius, cardY);
          ctx.lineTo(cardX + cardWidth - cornerRadius, cardY);
          ctx.quadraticCurveTo(cardX + cardWidth, cardY, cardX + cardWidth, cardY + cornerRadius);
          ctx.lineTo(cardX + cardWidth, cardY + headerHeight);
          ctx.lineTo(cardX, cardY + headerHeight);
          ctx.lineTo(cardX, cardY + cornerRadius);
          ctx.quadraticCurveTo(cardX, cardY, cardX + cornerRadius, cardY);
          ctx.fill();

          // Create gradient border for modern look
          const borderGradient = ctx.createLinearGradient(
            cardX,
            cardY,
            cardX + cardWidth,
            cardY + cardHeight,
          );
          borderGradient.addColorStop(0, palette.primary);
          borderGradient.addColorStop(0.3, palette.accent);
          borderGradient.addColorStop(0.7, palette.secondary);
          borderGradient.addColorStop(1, palette.primaryDark);

          ctx.strokeStyle = borderGradient;
          ctx.lineWidth = 3;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";

          // Draw border with subtle inset for depth
          drawRoundedRect(
            ctx,
            cardX + 2,
            cardY + 2,
            cardWidth - 4,
            cardHeight - 4,
            cornerRadius - 2,
          );
          ctx.stroke();

          // Add subtle inner border for refinement
          ctx.strokeStyle = palette.neutralUltraLight;
          ctx.lineWidth = 1;
          drawRoundedRect(
            ctx,
            cardX + 4,
            cardY + 4,
            cardWidth - 8,
            cardHeight - 8,
            cornerRadius - 3,
          );
          ctx.stroke();

          // Instructions section
          const instructionsY = cardY + headerHeight + 20;

          // Instructions title
          ctx.fillStyle = palette.primaryDark;
          ctx.font = '700 14px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "left";
          ctx.fillText(instructionsTitle, cardX + 30, instructionsY);

          // Instruction bullets
          ctx.fillStyle = palette.neutralDark;
          ctx.font = '500 11px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          instructions.forEach((instruction, index) => {
            if (instruction.trim() === "") return;
            ctx.fillText(
              instruction,
              cardX + 30,
              instructionsY + instructionStartOffset + index * instructionLineHeight,
            );
          });

          // QR Code section (larger for print clarity)
          const qrSectionY = instructionsY + qrSectionOffset;
          const qrSize = 190;
          const qrX = cardX + cardWidth - qrSize - 25;

          // QR background
          ctx.fillStyle = "#ffffff";
          ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
          ctx.shadowBlur = 8;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 2;
          drawRoundedRect(ctx, qrX - 10, qrSectionY - 10, qrSize + 20, qrSize + 48, 6);
          ctx.fill();

          // Reset shadow
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 0;

          // QR border
          ctx.strokeStyle = palette.neutralLight;
          ctx.lineWidth = 1;
          drawRoundedRect(ctx, qrX - 10, qrSectionY - 10, qrSize + 20, qrSize + 48, 6);
          ctx.stroke();

          // Draw QR code with high quality
          ctx.save();
          ctx.imageSmoothingEnabled = false; // Keep QR code crisp
          ctx.drawImage(qrImage, qrX, qrSectionY, qrSize, qrSize);
          ctx.restore();

          // QR labels
          ctx.fillStyle = palette.primaryDark;
          ctx.font = '700 11px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "center";
          ctx.fillText(qrCaption, qrX + qrSize / 2, qrSectionY + qrSize + 12);

          ctx.font = '500 9px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.fillStyle = palette.neutral;
          ctx.fillText(qrSubtitle, qrX + qrSize / 2, qrSectionY + qrSize + 25);

          // Footer - "Powered by Acceede.com"
          const footerY = cardY + cardHeight - 15;
          ctx.fillStyle = palette.neutral;
          ctx.font = '500 8px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "center";
          ctx.fillText("Powered by Acceede.com", cardX + cardWidth / 2, footerY);

          // Signature area at bottom left (adjusted for footer)
          const signatureX = cardX + 30;
          const signatureY = cardY + cardHeight - 75;
          const signatureWidth = 100;
          const signatureHeight = 30;

          if (school?.admin_signature?.path) {
            // Load and draw actual signature
            const signatureImage = new Image();
            signatureImage.crossOrigin = "anonymous";
            signatureImage.onload = () => {
              // Draw signature background
              ctx.fillStyle = palette.neutralUltraLight;
              ctx.fillRect(signatureX, signatureY, signatureWidth, signatureHeight);

              // Draw signature border
              ctx.strokeStyle = palette.neutralLight;
              ctx.lineWidth = 1;
              ctx.strokeRect(signatureX, signatureY, signatureWidth, signatureHeight);

              // Draw signature image with high quality
              ctx.save();
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = "high";
              ctx.drawImage(
                signatureImage,
                signatureX + 5,
                signatureY + 5,
                signatureWidth - 10,
                signatureHeight - 10,
              );
              ctx.restore();

              // Signature label
              ctx.fillStyle = palette.neutral;
              ctx.font =
                '500 9px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
              ctx.textAlign = "center";
              ctx.fillText(
                signatureLabel,
                signatureX + signatureWidth / 2,
                signatureY + signatureHeight + 12,
              );

              finalizeBothCards();
            };
            signatureImage.onerror = () => {
              // Fallback to signature line if image fails
              drawSignatureLine();
            };
            signatureImage.src = school.admin_signature.path;
          } else {
            // Draw signature line if no signature image available
            drawSignatureLine();
          }

          function drawSignatureLine() {
            // Signature line
            ctx.strokeStyle = palette.neutralLight;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(signatureX, signatureY + 15);
            ctx.lineTo(signatureX + signatureWidth, signatureY + 15);
            ctx.stroke();

            // Signature label
            ctx.fillStyle = palette.neutral;
            ctx.font = '500 9px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
            ctx.textAlign = "center";
            ctx.fillText(signatureLabel, signatureX + signatureWidth / 2, signatureY + 25);

            finalizeBothCards();
          }
        }

        function finalizeBothCards() {
          // Add card labels above each card
          ctx.fillStyle = colorPalette.primaryDark;
          ctx.font = '700 12px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "center";

          ctx.fillText("FRONT", frontCardX + cardWidth / 2, cardY - 25);
          ctx.fillText("BACK", backCardX + cardWidth / 2, cardY - 25);

          // Add cutting guides (dashed lines)
          ctx.strokeStyle = colorPalette.neutralLight;
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 5]);

          // Vertical cutting line
          const centerX = frontCardX + cardWidth + cardSpacing / 2;
          ctx.beginPath();
          ctx.moveTo(centerX, cardY - 10);
          ctx.lineTo(centerX, cardY + cardHeight + 10);
          ctx.stroke();

          ctx.setLineDash([]);

          // Generation info
          ctx.fillStyle = colorPalette.neutral;
          ctx.font = '500 9px "Inter", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif';
          ctx.textAlign = "center";
          ctx.fillText(
            `Generated: ${format(new Date(), "dd/MM/yyyy hh:mm a")}`,
            displayWidth / 2,
            cardY + cardHeight + 40,
          );

          resolve(canvas.toDataURL("image/png", 1.0));
        }
      } catch (error) {
        reject(error);
      }
    };

    qrImage.onerror = () => {
      reject(new Error("Failed to load QR code image"));
    };

    qrImage.src = qrCodeDataUrl;
  });
};

// Generate branded ID card for a single student
export const generateStudentIDCard = async (student, school, classInfo, qrCodeDataUrl) => {
  if (!student) return null;

  const fullName = [student.lastname, student.firstname, student.middlename]
    .filter(Boolean)
    .join(" ");

  return drawIDCard({
    subject: {
      photoPath: student?.picture?.path,
      fullName,
      gender: student.gender,
    },
    school,
    qrCodeDataUrl,
    cardTypeLabel: "STUDENT ID CARD",
    identityVerticalLabel: "STUDENT IDENTITY CARD",
    infoFields: [
      { label: "ADMISSION NO", value: student.registration_id || "N/A" },
      { label: "CLASS", value: classInfo?.class_name || "N/A" },
      { label: "GENDER", value: student.gender || "N/A" },
    ],
    emergencyLabel: "EMERGENCY",
    instructionsTitle: "IMPORTANT INSTRUCTIONS",
    instructions: [
      "• This ID Card must be with you at all times both",
      "  in and out of the school. Should you lose it, you",
      "  would be required to get a replacement immediately.",
      "",
      "• This ID Card is the property of",
      `  ${(school?.school_name || "SCHOOL").toUpperCase()}.`,
      "  If found, please return to the nearest Police",
      "  Station or the address on this Card.",
      "",
      "Phone: " + (school?.phone_number || "08100000088"),
      "Email: " + (school?.school_email || "support@acceede.com"),
    ],
    instructionStartOffset: 30,
    instructionLineHeight: 16,
    qrSectionOffset: 35,
    qrCaption: "QR CODE",
    qrSubtitle: "Scan for attendance",
    signatureLabel: "Principal Signature",
  });
};

const ROLE_LABELS = {
  0: "Admin",
  1: "Teacher",
  2: "Bursar",
  3: "Academic Admin",
  admin: "Admin",
  teacher: "Teacher",
  burser: "Bursar",
  academic_admin: "Academic Admin",
};

// Print-ready CR80 card (85.6mm × 53.98mm) at 300 DPI with 3mm bleed — the same spec and
// layout as the single student ID card in StudentIDModal.
const PRINT_SPECS = {
  cardWidthMM: 85.6,
  cardHeightMM: 53.98,
  bleedMM: 3,
  dpi: 300,
};
const mmToPx = (mm) => Math.round((mm / 25.4) * PRINT_SPECS.dpi);
const CARD_W = mmToPx(PRINT_SPECS.cardWidthMM);
const CARD_H = mmToPx(PRINT_SPECS.cardHeightMM);
const BLEED = mmToPx(PRINT_SPECS.bleedMM);
const SHEET_CARD_W = CARD_W + BLEED * 2;
const SHEET_CARD_H = CARD_H + BLEED * 2;
const SHEET_SPACING = CARD_W * 0.15;
const SHEET_W = SHEET_CARD_W * 2 + SHEET_SPACING;

// Physical size of the front + back sheet, so PDFs can place it at true size for cutting.
export const PRINT_SHEET_SIZE_MM = {
  width: (SHEET_W / PRINT_SPECS.dpi) * 25.4,
  height: (SHEET_CARD_H / PRINT_SPECS.dpi) * 25.4,
};

const rgb = (color) => `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
const FONT = '"Segoe UI", Arial, sans-serif';

// Shortens text with an ellipsis so it never runs past maxWidth.
const fitText = (ctx, text, maxWidth) => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let trimmed = text;
  while (trimmed.length > 1 && ctx.measureText(`${trimmed}…`).width > maxWidth) {
    trimmed = trimmed.slice(0, -1);
  }
  return `${trimmed}…`;
};

/**
 * Front + back print-ready card. Config:
 * {
 *   school, photoPath, qrCodeDataUrl,
 *   cardTypeLabel, verticalLabel,
 *   fields: [{ label, value, bold? }],  // up to 4, starting with the name
 *   emergencyLabel, instructionsTitle, instructions: string[] (blank = spacer, max 11),
 *   qrCaption, qrSubtitle, signatureLabel,
 * }
 * Resolves to a PNG data URL.
 */
const drawPrintReadyIDCard = async (config) => {
  const { school, photoPath, qrCodeDataUrl } = config;
  if (!qrCodeDataUrl || !school) return null;

  let palette = getDefaultColorPalette();
  if (school?.picture?.path) {
    try {
      palette = await extractLogoColors(school.picture.path);
    } catch (error) {
      console.error("Error extracting colors:", error);
    }
  }
  const primary = rgb(palette.primary);
  const primaryDarker = rgb(palette.primary.map((c) => Math.max(0, c - 30)));

  const loadOptional = async (src) => {
    if (!src) return null;
    try {
      return await loadImageWithTimeout(src, 5000);
    } catch (error) {
      console.log("Image failed to load:", src, error);
      return null;
    }
  };
  const [qrImage, logoImage, photoImage, signatureImage] = await Promise.all([
    loadImageWithTimeout(qrCodeDataUrl, 10000),
    loadOptional(school?.picture?.path),
    loadOptional(photoPath),
    loadOptional(school?.admin_signature?.path),
  ]);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = SHEET_W;
  canvas.height = SHEET_CARD_H;
  ctx.textBaseline = "top";
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(0, 0, SHEET_W, SHEET_CARD_H);

  const cornerRadius = CARD_W * 0.025;

  const drawCardBase = (cardX, cardY) => {
    ctx.fillStyle = "#ffffff";
    drawRoundedRect(ctx, cardX, cardY, CARD_W, CARD_H, cornerRadius);
    ctx.fill();
    ctx.strokeStyle = primary;
    ctx.lineWidth = 4;
    drawRoundedRect(ctx, cardX, cardY, CARD_W, CARD_H, cornerRadius);
    ctx.stroke();
  };

  const drawFront = (offsetX) => {
    const cardX = offsetX + BLEED;
    const cardY = BLEED;
    drawCardBase(cardX, cardY);

    // Header
    const headerHeight = CARD_H * 0.24;
    ctx.fillStyle = primary;
    ctx.fillRect(cardX, cardY, CARD_W, headerHeight);

    // School logo
    const logoSize = headerHeight * 0.65;
    const logoX = cardX + CARD_W * 0.04;
    const logoY = cardY + (headerHeight - logoSize) / 2;
    if (logoImage) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
      ctx.restore();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = `bold ${logoSize * 0.4}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("📚", logoX + logoSize / 2, logoY + logoSize / 2);
    }

    // School name, card type and session
    const textX = logoX + logoSize + CARD_W * 0.035;
    const textMaxWidth = cardX + CARD_W - CARD_W * 0.03 - textX;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${CARD_H * 0.055}px ${FONT}`;
    ctx.fillText(
      fitText(ctx, (school?.school_name || "SCHOOL NAME").toUpperCase(), textMaxWidth),
      textX,
      cardY + headerHeight * 0.18,
    );
    ctx.font = `${CARD_H * 0.042}px ${FONT}`;
    ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
    ctx.fillText(config.cardTypeLabel, textX, cardY + headerHeight * 0.48);
    const year = new Date().getFullYear();
    ctx.font = `${CARD_H * 0.038}px ${FONT}`;
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.fillText(
      school?.settings?.current_session || `${year}/${year + 1}`,
      textX,
      cardY + headerHeight * 0.72,
    );

    // Vertical identity label
    ctx.save();
    ctx.translate(cardX + CARD_W * 0.025, cardY + headerHeight + CARD_H * 0.35);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = primary;
    ctx.font = `bold ${CARD_H * 0.042}px ${FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(config.verticalLabel, 0, 0);
    ctx.restore();

    // Photo
    const photoWidth = CARD_W * 0.26;
    const photoHeight = CARD_H * 0.55;
    const photoX = cardX + CARD_W * 0.075;
    const photoY = cardY + headerHeight + CARD_H * 0.06;
    if (photoImage) {
      ctx.save();
      drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, cornerRadius * 0.8);
      ctx.clip();
      const aspectRatio = photoImage.width / photoImage.height;
      let drawWidth = photoWidth;
      let drawHeight = photoHeight;
      let drawX = photoX;
      let drawY = photoY;
      if (aspectRatio > photoWidth / photoHeight) {
        drawWidth = photoHeight * aspectRatio;
        drawX = photoX - (drawWidth - photoWidth) / 2;
      } else {
        drawHeight = photoWidth / aspectRatio;
        drawY = photoY - (drawHeight - photoHeight) / 2;
      }
      ctx.drawImage(photoImage, drawX, drawY, drawWidth, drawHeight);
      ctx.restore();
      ctx.strokeStyle = "#e5e7eb";
      ctx.lineWidth = 3;
      drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, cornerRadius * 0.8);
      ctx.stroke();
    } else {
      ctx.fillStyle = "#f3f4f6";
      drawRoundedRect(ctx, photoX, photoY, photoWidth, photoHeight, cornerRadius * 0.8);
      ctx.fill();
      ctx.fillStyle = "#9ca3af";
      ctx.font = `${photoHeight * 0.3}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("👤", photoX + photoWidth / 2, photoY + photoHeight / 2);
    }

    // Fields
    const infoX = photoX + photoWidth + CARD_W * 0.045;
    const infoRight = cardX + CARD_W - CARD_W * 0.04;
    const lineHeight = CARD_H * 0.115;
    config.fields.slice(0, 4).forEach((field, index) => {
      const y = photoY + lineHeight * index;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#6b7280";
      ctx.font = `bold ${CARD_H * 0.032}px ${FONT}`;
      ctx.fillText(field.label, infoX, y);
      ctx.fillStyle = "#111827";
      ctx.font = `${field.bold ? "bold " : ""}${CARD_H * 0.045}px ${FONT}`;
      ctx.fillText(fitText(ctx, String(field.value), infoRight - infoX), infoX, y + CARD_H * 0.048);
      ctx.strokeStyle = "#e5e7eb";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(infoX, y + CARD_H * 0.095);
      ctx.lineTo(infoRight, y + CARD_H * 0.095);
      ctx.stroke();
    });

    // Emergency contact
    const emergencyY = cardY + CARD_H - CARD_H * 0.14;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#dc2626";
    ctx.font = `bold ${CARD_H * 0.038}px ${FONT}`;
    ctx.fillText(config.emergencyLabel, photoX, emergencyY);
    ctx.fillStyle = "#111827";
    ctx.font = `${CARD_H * 0.038}px ${FONT}`;
    ctx.fillText(school?.phone_number || "08100000088", photoX, emergencyY + CARD_H * 0.052);

    // Address footer
    const address = formatAddress(school?.address?.main);
    if (school?.address?.main && address) {
      ctx.fillStyle = "#6b7280";
      ctx.font = `${CARD_H * 0.03}px ${FONT}`;
      ctx.textAlign = "center";
      ctx.fillText(
        fitText(ctx, address, CARD_W * 0.9),
        cardX + CARD_W / 2,
        cardY + CARD_H - CARD_H * 0.033,
      );
    }
  };

  const drawBack = (offsetX) => {
    const cardX = offsetX + BLEED;
    const cardY = BLEED;
    drawCardBase(cardX, cardY);

    // Header strip
    const headerHeight = CARD_H * 0.12;
    const headerGradient = ctx.createLinearGradient(cardX, cardY, cardX, cardY + headerHeight);
    headerGradient.addColorStop(0, primary);
    headerGradient.addColorStop(1, primaryDarker);
    ctx.fillStyle = headerGradient;
    ctx.fillRect(cardX, cardY, CARD_W, headerHeight);

    // QR code (placed first so the instructions can stop short of it)
    const qrSize = CARD_H * 0.5;
    const qrX = cardX + CARD_W - qrSize - CARD_W * 0.08;
    const instructionsY = cardY + headerHeight + CARD_H * 0.05;
    const qrY = instructionsY + CARD_H * 0.08;

    // Instructions
    const textX = cardX + CARD_W * 0.05;
    const textMaxWidth = qrX - CARD_W * 0.04 - textX;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#111827";
    ctx.font = `bold ${CARD_H * 0.048}px ${FONT}`;
    ctx.fillText(config.instructionsTitle, textX, instructionsY);
    ctx.fillStyle = "#374151";
    ctx.font = `${CARD_H * 0.035}px ${FONT}`;
    config.instructions.slice(0, 11).forEach((line, index) => {
      if (line.trim() === "") return;
      ctx.fillText(
        fitText(ctx, line, textMaxWidth),
        textX,
        instructionsY + CARD_H * 0.08 + index * CARD_H * 0.048,
      );
    });

    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = "rgba(0, 0, 0, 0.1)";
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 2;
    drawRoundedRect(
      ctx,
      qrX - CARD_W * 0.02,
      qrY - CARD_H * 0.02,
      qrSize + CARD_W * 0.04,
      qrSize + CARD_H * 0.11,
      cornerRadius * 0.5,
    );
    ctx.fill();
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
    ctx.restore();

    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#111827";
    ctx.font = `bold ${CARD_H * 0.042}px ${FONT}`;
    ctx.fillText(config.qrCaption, qrX + qrSize / 2, qrY + qrSize + CARD_H * 0.018);
    ctx.fillStyle = "#6b7280";
    ctx.font = `${CARD_H * 0.032}px ${FONT}`;
    ctx.fillText(config.qrSubtitle, qrX + qrSize / 2, qrY + qrSize + CARD_H * 0.062);

    // Signature
    const sigX = cardX + CARD_W * 0.05;
    const sigY = cardY + CARD_H - CARD_H * 0.19;
    const sigWidth = CARD_W * 0.25;
    const sigHeight = CARD_H * 0.11;
    if (signatureImage) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(sigX, sigY, sigWidth, sigHeight);
      ctx.strokeStyle = "#d1d5db";
      ctx.lineWidth = 2;
      ctx.strokeRect(sigX, sigY, sigWidth, sigHeight);
      ctx.drawImage(
        signatureImage,
        sigX + sigWidth * 0.05,
        sigY + sigHeight * 0.1,
        sigWidth * 0.9,
        sigHeight * 0.8,
      );
    } else {
      ctx.strokeStyle = "#9ca3af";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sigX, sigY + sigHeight / 2);
      ctx.lineTo(sigX + sigWidth, sigY + sigHeight / 2);
      ctx.stroke();
    }
    ctx.fillStyle = "#6b7280";
    ctx.font = `${CARD_H * 0.032}px ${FONT}`;
    ctx.textAlign = "center";
    ctx.fillText(config.signatureLabel, sigX + sigWidth / 2, sigY + sigHeight + CARD_H * 0.018);

    // Footer
    ctx.fillStyle = "#9ca3af";
    ctx.font = `${CARD_H * 0.028}px ${FONT}`;
    ctx.fillText("Powered by Acceede.com", cardX + CARD_W / 2, cardY + CARD_H - CARD_H * 0.028);
  };

  const drawCropMarks = (offsetX) => {
    ctx.strokeStyle = "#94a3b8";
    ctx.lineWidth = 2;
    const markLength = BLEED * 0.8;
    [
      [0, 0],
      [CARD_W, 0],
      [0, CARD_H],
      [CARD_W, CARD_H],
    ].forEach(([dx, dy]) => {
      const x = offsetX + BLEED + dx;
      const y = BLEED + dy;
      const xDir = dx === 0 ? -1 : 1;
      const yDir = dy === 0 ? -1 : 1;
      ctx.beginPath();
      ctx.moveTo(x + xDir * markLength, y);
      ctx.lineTo(x, y);
      ctx.lineTo(x, y + yDir * markLength);
      ctx.stroke();
    });
  };

  const backOffset = SHEET_CARD_W + SHEET_SPACING;
  drawFront(0);
  drawBack(backOffset);
  drawCropMarks(0);
  drawCropMarks(backOffset);

  ctx.fillStyle = "#475569";
  ctx.font = `bold ${CARD_H * 0.04}px ${FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("FRONT", SHEET_CARD_W / 2, BLEED * 0.3);
  ctx.fillText("BACK", backOffset + SHEET_CARD_W / 2, BLEED * 0.3);
  ctx.fillStyle = "#94a3b8";
  ctx.font = `${CARD_H * 0.028}px ${FONT}`;
  ctx.fillText(
    `Generated: ${format(new Date(), "dd/MM/yyyy hh:mm a")}`,
    SHEET_W / 2,
    SHEET_CARD_H - BLEED * 0.4,
  );

  return canvas.toDataURL("image/png", 1.0);
};

// Generate branded ID card for a single staff member — same design as the student ID card.
export const generateStaffIDCard = async (staff, school, qrCodeDataUrl) => {
  if (!staff) return null;

  const fullName = [staff.lastname, staff.firstname, staff.middlename]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();

  const roleText =
    ROLE_LABELS[staff.role] ||
    (typeof staff.role === "string" ? staff.role.replace("_", " ") : "Staff");

  const schoolName = (school?.school_name || "SCHOOL").toUpperCase();

  return drawPrintReadyIDCard({
    school,
    photoPath: staff?.photo || staff?.picture?.path,
    qrCodeDataUrl,
    cardTypeLabel: "STAFF ID CARD",
    verticalLabel: "STAFF IDENTITY CARD",
    fields: [
      { label: "NAME", value: fullName || "N/A", bold: true },
      { label: "STAFF ID", value: staff.staff_id || staff.staffId || "N/A" },
      { label: "ROLE", value: roleText.toUpperCase() },
      { label: "GENDER", value: staff.gender || "N/A" },
    ],
    emergencyLabel: "EMERGENCY",
    instructionsTitle: "IMPORTANT INSTRUCTIONS",
    instructions: [
      "• This ID Card is the official property of:",
      `  ${schoolName}`,
      "• It must be carried while on duty and shown",
      "  whenever requested.",
      "• If lost, report to the school administration",
      "  immediately for a replacement.",
      "• If found, please return it to the address on",
      "  this card or the nearest Police Station.",
      "",
      "Contact: " + (school?.phone_number || "08100000088"),
      school?.school_email ? "Email: " + school.school_email : "",
    ],
    qrCaption: "QR CODE",
    qrSubtitle: "Scan to clock in / out",
    signatureLabel: "Principal Signature",
  });
};

// Backwards-compatibility alias
export const generateTeacherIDCard = generateStaffIDCard;
